import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Alert, ActivityIndicator, Dimensions, Platform, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import { DeliveryTracker } from '../components/DeliveryTracker';
import { InAppBrowserModal } from '../components/ui/InAppBrowserModal';
import { spacing } from '../theme';
import { useColors, ThemeColors } from '../hooks/useColors';
import { RootStackParamList } from '../navigation/types';
import { supabase } from '../lib/supabase';

type OrderTrackingScreenRouteProp = RouteProp<RootStackParamList, 'OrderTracking'>;

const SCREEN_HEIGHT = Dimensions.get('window').height;

export default function OrderTrackingScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const route = useRoute<OrderTrackingScreenRouteProp>();
  const navigation = useNavigation();
  const { deliveryId, orderId } = route.params;
  const [trackingUrl, setTrackingUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);

  useEffect(() => {
    const fetchTrackingUrl = async () => {
      if (!deliveryId) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('deliveries')
          .select('tracking_url')
          .eq('delivery_id', deliveryId)
          .single();

        if (error) throw error;
        
        if (data?.tracking_url) {
          setTrackingUrl(data.tracking_url);
        } else {
          setError('Tracking URL not available yet');
        }
      } catch (err) {
        console.error('Error fetching tracking URL:', err);
        setError('Failed to load tracking information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrackingUrl();
  }, [deliveryId]);

  const handleOpenTracking = () => {
    if (!trackingUrl) {
      Alert.alert('Error', 'Tracking URL is not available yet');
      return;
    }
    setShowTrackingModal(true);
  };

  React.useLayoutEffect(() => {
    navigation.setOptions({
      title: 'Order Tracking',
      headerRight: () => (
        <MaterialIcons 
          name="info-outline" 
          size={24} 
          color={colors.primary} 
          style={{ marginRight: 16 }}
          onPress={() => {
            Alert.alert(
              'Delivery Information',
              `Delivery ID: ${deliveryId}\n` +
              `Order ID: ${orderId || 'N/A'}`
            );
          }}
        />
      ),
    });
  }, [navigation, deliveryId, orderId]);

  if (!deliveryId) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <MaterialIcons name="error-outline" size={48} color={colors.error} />
          <Text style={styles.errorText}>Missing delivery information</Text>
          <Text style={styles.errorDetails}>
            No delivery ID was provided. Please go back and try again.
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const renderTrackingContent = () => {
    if (isLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading tracking information...</Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.errorContainer}>
          <MaterialIcons name="error-outline" size={48} color={colors.error} />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      );
    }

    return (
      <View style={styles.trackingContainer}>
        <Text style={styles.trackingTitle}>Track Your Delivery</Text>
        <Text style={styles.trackingDescription}>
          Click the button below to open the delivery tracking page in your browser.
        </Text>
        <TouchableOpacity 
          style={[
            styles.trackingButton,
            !trackingUrl && styles.trackingButtonDisabled
          ]}
          onPress={handleOpenTracking}
          disabled={!trackingUrl}
        >
          <MaterialIcons name="map" size={24} color={colors.white} />
          <Text style={styles.trackingButtonText}>
            {trackingUrl ? 'Open Tracking Page' : 'Tracking Unavailable'}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.headerContainer}>
        <Text style={styles.headerTitle}>Delivery Status</Text>
        <Text style={styles.headerSubtitle}>
          Order #{orderId?.toString().substring(0, 8) || 'N/A'}
        </Text>
      </View>
      
      <View style={styles.trackerContainer}>
        <DeliveryTracker deliveryId={deliveryId} />
      </View>

      {renderTrackingContent()}

      <View style={styles.helpContainer}>
        <Text style={styles.helpTitle}>Need Help?</Text>
        <Text style={styles.helpText}>
          For issues with your delivery, please contact our support team or 
          the merchant directly. You can track your delivery in real-time using
          the tracking link above.
        </Text>
      </View>

      <InAppBrowserModal
        visible={showTrackingModal}
        url={trackingUrl}
        title="Delivery Tracking"
        onClose={() => setShowTrackingModal(false)}
      />
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  headerContainer: {
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.white,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text,
    marginBottom: spacing.xs,
  },
  headerSubtitle: {
    fontSize: 16,
    color: colors.textLight,
  },
  trackerContainer: {
    padding: spacing.sm,
  },
  trackingContainer: {
    margin: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
  },
  trackingTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginBottom: spacing.sm,
  },
  trackingDescription: {
    fontSize: 16,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  trackingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    marginTop: spacing.sm,
  },
  trackingButtonDisabled: {
    backgroundColor: colors.textLight,
  },
  trackingButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginLeft: spacing.sm,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    backgroundColor: colors.background,
    margin: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  loadingText: {
    marginTop: spacing.md,
    color: colors.textLight,
    fontSize: 16,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    backgroundColor: colors.background,
    margin: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  errorText: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.error,
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  errorDetails: {
    fontSize: 16,
    color: colors.textLight,
    textAlign: 'center',
  },
  helpContainer: {
    margin: spacing.md,
    backgroundColor: colors.backgroundLight,
    borderRadius: 8,
    padding: spacing.md,
  },
  helpTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.primary,
    marginBottom: spacing.sm,
  },
  helpText: {
    fontSize: 14,
    color: colors.textLight,
    lineHeight: 20,
  },
}); 